import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button, ButtonGroup,
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input,
  Table
} from 'reactstrap';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, parseISO, formatISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { FaCalendarAlt, FaDownload, FaUser, FaFileAlt, FaPrint } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useReactToPrint } from 'react-to-print';
import { clientActions } from '../../sagas/clientSlice';
import Select from "react-select";
import autoTable from 'jspdf-autotable';

const VentesStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);
  const [blFilter, setBlFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [chantierFilter, setChantierFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const [filters, setFilters] = useState({
    clientId: null,
    chantierId: null,
    dateDebut: formatDate(todayDate),
    dateFin: formatDate(tomorrowDate),
    query: ''
  });

  useEffect(() => {
    dispatch(saleActions.clearClientReport());
  }, []);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();

    if (dateString.includes('-')) {
      return parseISO(dateString);
    }

    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  // Client report state
  const { clients, loading } = useSelector(state => state.clients);
  const { clientReport, clientReportLoading, clientReportError } = useSelector(state => state.sales);
  const [selectedClient, setSelectedClient] = useState('');
  const clientReportRef = useRef(null);

  const clientOptions = clients?.map(client => ({
    value: client.id,
    label: client.typeClient === 'PARTICULIER'
      ? `${client.nom} ${client.prenom || ''} `
      : client.entreprise,
    chantiers: client.chantiers || []
  })) ?? [];

  const clientOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...clientOptions
  ];

  const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
    value: chantier.id,
    label: `${chantier.code} - ${chantier.nom}`,
    ...chantier
  })) || [];

  const chantierOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...chantierOptions
  ];

  // Effect for fetching data
  useEffect(() => {
    if (clients.length === 0) {
      // Fetch clients for client report
      const fetchClients = async () => {
        dispatch(clientActions.search({
          query: ""
        }));
      };

      fetchClients();
    }
  }, [dispatch]);


  // Generate client report
  const generateClientReport = () => {
    dispatch(saleActions.generateClientReport({ ...filters }));
  };

  // Print client report
  const handlePrintClientReport = useReactToPrint({
    content: () => clientReportRef.current,
    documentTitle: 'Rapport_Detail_Vente_Client',
    pageStyle: '@page { size: landscape; }',
  });

  const exportClientReportToPDF = () => {
    if (!clientReport) return;

    // Portrait layout
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const footerHeight = 15;

    // Palette
    const primaryColor = [41, 128, 185];
    const secondaryColor = [52, 152, 219];
    const accentColor = [39, 174, 96];
    const negativeColor = [231, 76, 60];
    const lightGray = [245, 245, 245];
    const lineGray = [220, 226, 233];

    // Presence flags
    const hasClient = !!(clientReport?.client && clientReport.client.name && clientReport.client.name !== "Passager" && clientReport.summary);

    // Resolve magasin name from common places
    const magasinName = clientReport?.magasin;

    // Helpers
    const fmtDate = (d) => format(parseDate(d), 'dd/MM/yyyy', { locale: fr });

    const drawChip = (x, y, label, value) => {
      if (!value) return;
      const padX = 3, padY = 2;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      const labelW = doc.getTextWidth(label.toUpperCase());
      doc.setFont('helvetica', 'normal');
      const valueW = doc.getTextWidth(`: ${value}`);
      const w = labelW + valueW + padX * 2 + 2;
      const h = 6;

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.roundedRect(x, y, w, h, 2, 2, 'FD');

      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFont('helvetica', 'bold');
      doc.text(label.toUpperCase(), x + padX, y + h - padY - 0.5);
      doc.setFont('helvetica', 'normal');
      doc.text(`: ${value}`, x + padX + labelW, y + h - padY - 0.5);
      doc.setTextColor(0, 0, 0);
    };

    const addPageHeader = () => {
      // Header band
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 28, 'F');
      doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.rect(0, 23, pageWidth, 5, 'F');

      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('RAPPORT VENTE CLIENT', pageWidth / 2, 14, { align: 'center' });

      // Period
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const formattedDateFrom = fmtDate(clientReport.period.from);
      const formattedDateTo = fmtDate(clientReport.period.to);
      doc.text(`Période: du ${formattedDateFrom} au ${formattedDateTo}`, pageWidth / 2, 20, { align: 'center' });

      // Chips row (magasin + dates)
      let chipX = margin;
      const chipY = 31;
      if (magasinName) {
        drawChip(chipX, chipY, 'Magasin', magasinName);
        chipX += 4 + doc.getTextWidth('MAGASIN') + doc.getTextWidth(`: ${magasinName}`) + 14;
      }
      drawChip(chipX, chipY, 'Du', formattedDateFrom);
      chipX += 4 + doc.getTextWidth('DU') + doc.getTextWidth(`: ${formattedDateFrom}`) + 14;
      drawChip(chipX, chipY, 'Au', formattedDateTo);
    };

    const addPageFooter = () => {
      const footerY = pageHeight - footerHeight;
      doc.setDrawColor(lineGray[0], lineGray[1], lineGray[2]);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY, pageWidth - margin, footerY);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(company.name, margin, footerY + 5);
      if (magasinName) doc.text(`Magasin: ${magasinName}`, margin, footerY + 10);
      doc.text(`Document généré le: ${clientReport.generationDate}`, pageWidth - margin, footerY + 5, { align: 'right' });
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, footerY + 5, { align: 'center' });
    };

    // Header
    addPageHeader();

    // Company block
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(company.name, margin, 48);
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, 49.5, margin + 60, 49.5);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (company.subtitle) doc.text(company.subtitle, margin, 54);
    if (company.address) doc.text(`Adresse: ${company.address}`, margin, 59);
    const phoneEmail = [company.phone1, company.email].filter(Boolean).join(' | ');
    if (phoneEmail) doc.text(phoneEmail, margin, 64);

    // Client info card (only if client exists)
    if (hasClient) {
      doc.setFillColor(248, 249, 250);
      doc.setDrawColor(lineGray[0], lineGray[1], lineGray[2]);

      const clientBoxHeight = 34;
      const clientBoxY = 42;
      doc.roundedRect(pageWidth - margin - 90, clientBoxY, 90, clientBoxHeight, 3, 3, 'FD');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('Informations Client', pageWidth - margin - 86, clientBoxY + 7);
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.line(pageWidth - margin - 86, clientBoxY + 9, pageWidth - margin - 15, clientBoxY + 9);

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Client: ${clientReport.client.name || ''}`, pageWidth - margin - 86, clientBoxY + 16);
      if (clientReport.client.phone) doc.text(`Téléphone: ${clientReport.client.phone}`, pageWidth - margin - 86, clientBoxY + 21);
      if (clientReport.client.code) doc.text(`Code client: ${clientReport.client.code}`, pageWidth - margin - 86, clientBoxY + 26);
    }

    // ===== TABLE (Portrait + remove "Chantier" and "UM") =====
    // Columns now: N° BL | Date | Désignation | Quantité | PU | Total
    const headers = ['N° BL', 'Date', 'Désignation', 'Quantité', 'PU', 'Total'];

    const body = clientReport.deliveryNotes.map(note => ([
      note.blNumber,
      fmtDate(note.date),
      note.designation,
      String(note.quantity ?? ''),
      formatPrice(note.unitPrice, false),
      formatPrice(note.lineTotal, false)
    ]));

    // Column widths for 6 columns (sum ≈ 1.00)
    const tableWidth = pageWidth - (2 * margin);
    const colW = {
      num: tableWidth * 0.14,      // N° BL
      date: tableWidth * 0.14,     // Date
      des: tableWidth * 0.38,      // Désignation
      qty: tableWidth * 0.10,      // Quantité
      pu: tableWidth * 0.12,       // PU
      total: tableWidth * 0.12     // Total
    };

    // Total for foot row
    const totalAmount = clientReport.deliveryNotes.reduce(
      (acc, note) => acc + (Number(note.lineTotal) || 0),
      0
    );

    const footRow = ['', '', '', '', 'Total:', formatPrice(totalAmount, false)];

    // Start table below header cards
    const startY = hasClient ? 82 : 72;

    autoTable(doc, {
      startY,
      head: [headers],
      body,
      foot: [footRow],
      theme: 'grid',
      tableLineColor: lineGray,
      tableLineWidth: 0.2,
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 9
      },
      footStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 10
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: colW.num },
        1: { halign: 'center', cellWidth: colW.date },
        2: { halign: 'left', cellWidth: colW.des },
        3: { halign: 'center', cellWidth: colW.qty },
        4: { halign: 'right', cellWidth: colW.pu },
        5: { halign: 'right', cellWidth: colW.total }
      },
      alternateRowStyles: { fillColor: lightGray },
      styles: { overflow: 'linebreak', cellPadding: 3, fontSize: 9 },
      margin: { top: margin, right: margin, bottom: footerHeight, left: margin },
      didDrawPage: () => { addPageFooter(); addPageHeader(); } // header + footer on each page
    });

    // ===== SUMMARY (only when client exists) =====
    if (hasClient) {
      let summaryY = doc.lastAutoTable.finalY + 12;
      if (summaryY > pageHeight - 80) {
        doc.addPage();
        addPageHeader();
        summaryY = 40;
      }

      // Summary box (left)
      doc.setFillColor(248, 249, 250);
      doc.roundedRect(margin, summaryY, pageWidth * 0.42, 50, 3, 3, 'F');

      const formattedDateFrom = fmtDate(clientReport.period.from);
      const formattedDateTo = fmtDate(clientReport.period.to);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('RÉSUMÉ', margin + 5, summaryY + 8);
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.line(margin + 5, summaryY + 10, margin + 35, summaryY + 10);

      doc.setTextColor(0, 0, 0);

      const summaryData = [
        ['Montant Total Consommé', formatPrice(clientReport.summary.totalConsome, false)],
        [`Solde Avant le ${formattedDateFrom}`, formatPrice(clientReport.summary.soldeAvant, false)],
        ['Paiements', formatPrice(clientReport.summary.reglements, false)],
        ['Avoir', formatPrice(clientReport.summary.avoir, false)]
      ];

      autoTable(doc, {
        startY: summaryY + 14,
        body: summaryData,
        theme: 'plain',
        styles: { cellPadding: 3, fontSize: 10 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: pageWidth * 0.27 },
          1: { halign: 'right', cellWidth: pageWidth * 0.15 }
        },
        tableWidth: pageWidth * 0.42,
        margin: { left: margin }
      });

      const netAPayerY = doc.lastAutoTable.finalY + 5;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Net A Payer', margin + 5, netAPayerY);

      const netAPayerAmount = parseFloat(clientReport.summary.netApaye);
      if (netAPayerAmount > 0) {
        doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
        doc.text(`+${formatPrice(netAPayerAmount, false)}`, margin + pageWidth * 0.42 - 5, netAPayerY, { align: 'right' });
      } else if (netAPayerAmount < 0) {
        doc.setTextColor(negativeColor[0], negativeColor[1], negativeColor[2]);
        doc.text(formatPrice(netAPayerAmount, false), margin + pageWidth * 0.42 - 5, netAPayerY, { align: 'right' });
      } else {
        doc.setTextColor(0, 0, 0);
        doc.text(formatPrice(0, false), margin + pageWidth * 0.42 - 5, netAPayerY, { align: 'right' });
      }

      // Amount in words (right)
      const amountY = summaryY;
      const boxW = pageWidth * 0.42;
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
      doc.roundedRect(pageWidth - margin - boxW, amountY, boxW, 42, 3, 3, 'FD');

      doc.setFontSize(10.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      doc.text('Arrêté le présent document à la somme de :', pageWidth - margin - boxW + 5, amountY + 10);

      if (netAPayerAmount < 0) doc.setTextColor(negativeColor[0], negativeColor[1], negativeColor[2]);
      else doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);

      doc.setFont('helvetica', 'italic');
      const amountWords = netAPayerAmount < 0
        ? `Moins ${convertNumberToWords(Math.abs(netAPayerAmount))}`
        : convertNumberToWords(netAPayerAmount);
      doc.text(`${amountWords} Dirhams`,
        pageWidth - margin - boxW + 5, amountY + 20,
        { maxWidth: boxW - 10 });
    }

    // Footer on last page
    addPageFooter();

    // Filename includes magasin if available
    const sanitizedClientName = hasClient
      ? String(clientReport.client.name).replace(/[^a-z0-9]/gi, '_').toLowerCase()
      : 'sans_client';

    try {
      const blobUrl = doc.output('bloburl');        // creates a Blob URL
      const opened = window.open(blobUrl, '_blank'); // try opening a new tab
      if (!opened) {
        // Fallback if popup is blocked: create a temporary link
        const a = document.createElement('a');
        a.href = blobUrl;
        a.target = '_blank';
        a.rel = 'noopener';
        a.click();
      }
    } catch (e) {
      // Last-resort fallback
      doc.output('dataurlnewwindow');
    }
  };


  // Filter states for BL number and designation


  // Filtered delivery notes based on BL number, client, chantier, and designation
  let filteredDeliveryNotes = [];
  if (clientReport && clientReport.deliveryNotes) {
    filteredDeliveryNotes = clientReport.deliveryNotes.filter(note => {
      const blMatch = blFilter ? (note.blNumber || '').toUpperCase().includes(blFilter) : true;
      const clientMatch = clientFilter ? (note.client?.name || '').toUpperCase().includes(clientFilter) : true;
      const chantierMatch = chantierFilter ? (note.chantier?.nom || '').toUpperCase().includes(chantierFilter) : true;
      const designationMatch = designationFilter ? (note.designation || '').toUpperCase().includes(designationFilter) : true;
      return blMatch && clientMatch && chantierMatch && designationMatch;
    });
  }

  return (
    <Container fluid className="page-content">
      <Card className="shadow-sm mb-4">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaFileAlt className="me-2 text-primary" />
              Détails des ventes
            </h5>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-4">
            <Col md={3}>
              <FormGroup>
                <Label for="clientSelect" className="form-label">
                  <FaUser className="me-1" /> Client
                </Label>
                <Select
                  id="clientId"
                  options={clientOptionsWithAll}
                  value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                  onChange={(selectedOption) => {
                    setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                    setSelectedClient(selectedOption);
                  }}
                  placeholder={t('bonLivraison.selectClient')}
                  isSearchable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="dateDebut"
                  value={filters.dateDebut}
                  onChange={e => setFilters(f => ({ ...f, dateDebut: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="dateFin"
                  value={filters.dateFin}
                  onChange={e => setFilters(f => ({ ...f, dateFin: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={3} className="d-flex mb-3 align-items-end">
              <Button
                color="success"
                className="me-2"
                size='sm'
                onClick={generateClientReport}
                disabled={clientReportLoading}
              >
                {clientReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Génération...
                  </>
                ) : (
                  <>
                    <FaFileAlt className="me-1" /> Générer
                  </>
                )}
              </Button>
              <Button
                color="secondary"
                size='sm'
                onClick={exportClientReportToPDF}
              >
                <FaDownload className="me-1" /> Télécharger
              </Button>
            </Col>
          </Row>

          {/* Client Report Content */}
          <div ref={clientReportRef}>
            {clientReport ? (
              <div className="client-report p-4 border rounded-4 bg-white shadow-sm animate__animated animate__fadeIn">
                {/* <div className="report-header mb-4 pb-2 border-bottom">
                  <div className="text-center mb-3">
                    <h4 className="text-primary mb-1 fw-bold letter-spacing-1">RAPPORT VENTE CLIENT</h4>
                    <p className="text-muted small mb-0">
                      <FaCalendarAlt className="me-1" />
                      Période: <span className="fw-semibold">du {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                    </p>
                  </div>

                  <Row className="mb-3 g-4">
                    <Col md={6}>
                      <div className="company-info bg-light p-3 rounded-3 h-100">
                        <h5 className="mb-2 text-dark fw-bold"><FaFileAlt className="me-2 text-primary" />{company.name}</h5>
                        <div className="small text-secondary">
                          <div><span className="fw-semibold">Adresse:</span> {company.address}</div>
                          <div><span className="fw-semibold">Tél:</span> {company.phone1} <span className="mx-1">|</span> <span className="fw-semibold">Email:</span> {company.email}</div>
                          <div><span className="fw-semibold">RC:</span> {company.rc} <span className="mx-1">|</span> <span className="fw-semibold">IF:</span> {company.identificationFiscale} <span className="mx-1">|</span> <span className="fw-semibold">ICE:</span> {company.ice}</div>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="client-info bg-white border border-2 border-primary p-3 rounded-3 h-100">
                        <h6 className="mb-2 text-primary fw-bold"><FaUser className="me-2" />Informations Client</h6>
                        <div className="small">
                          <div className="mb-1"><strong>Client:</strong> {clientReport.client.name}</div>
                          <div className="mb-1"><strong>Téléphone:</strong> {clientReport.client.phone}</div>
                          <div className="mb-0"><strong>Code client:</strong> {clientReport.client.code}</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div> */}

                <div className="report-body mb-4">
                  <div className="table-responsive rounded-3 overflow-hidden">
                    {/* Filter fields for BL number and product designation */}
                    <div className="d-flex gap-3 mb-2 flex-wrap">
                      <Input
                        type="text"
                        placeholder="Filtrer par N° BL..."
                        value={blFilter || ''}
                        onChange={e => setBlFilter(e.target.value.toUpperCase())}
                        className="border border-2 rounded-3 px-3 text-uppercase fw-bold"
                        style={{ maxWidth: 160 }}
                      />
                      <Input
                        type="text"
                        placeholder="Filtrer par Client..."
                        value={clientFilter || ''}
                        onChange={e => setClientFilter(e.target.value.toUpperCase())}
                        className="border border-2 rounded-3 px-3 text-uppercase fw-bold"
                        style={{ maxWidth: 180 }}
                      />

                      <Input
                        type="text"
                        placeholder="Filtrer par Désignation..."
                        value={designationFilter || ''}
                        onChange={e => setDesignationFilter(e.target.value.toUpperCase())}
                        className="border border-2 rounded-3 px-3 text-uppercase fw-bold"
                        style={{ maxWidth: 200 }}
                      />
                    </div>
                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center">N° BL</th>
                          <th className="text-center">Client</th>
                          <th className="text-center">Date</th>
                          <th className="text-center">Désignation</th>
                          <th className="text-center">Quantité</th>
                          <th className="text-center">UM</th>
                          <th className="text-end">PU</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDeliveryNotes.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            <td className="text-center fw-semibold">{note.blNumber}</td>
                            <td className="text-center">{note.client.name}</td>
                            <td className="text-center">{format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr })}</td>
                            <td className="ps-2">{note.designation}</td>
                            <td className="text-center">{note.quantity}</td>
                            <td className="text-center">{note.unit}</td>
                            <td className="text-end">{formatPrice(note.unitPrice, false)}</td>
                            <td className="text-end fw-bold">{formatPrice(note.lineTotal, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="4" className="text-end fw-bold">Total:</td>
                          <td className="text-center fw-bold text-primary">
                            {filteredDeliveryNotes.reduce((acc, note) => acc + (Number(note.quantity) || 0), 0)}
                          </td>
                          <td></td>
                          <td></td>
                          <td className="text-end fw-bold text-primary">{formatPrice(filteredDeliveryNotes.reduce((acc, note) => acc + (note.lineTotal || 0), 0), false)}</td>
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                </div>


                {clientReport && clientReport.summary && <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(clientReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(clientReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Paiements</td>
                              <td className="text-end">{formatPrice(clientReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                clientReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">Arrêté le présent document à la somme de:</p>
                        {
                          clientReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>}

                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{clientReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {clientReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un client et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style jsx>{`
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default VentesStatistiques;
